import LinkButton from '@carvana/ds-link-button';
import SelectableTile from '@carvana/ds-selectable-tile';
import cx from 'classnames';
import omit from 'lodash.omit';
import { ChevronDown } from 'lucide-react';
import React, { useContext, useState } from 'react';
import {
  CardImage,
  HeadlineRow,
  ImageContainer,
  ImageUnavailable,
  PriceContainer,
  PriceLabel,
  PriceValue,
  StockCount,
  Title
} from '@/new-cars/common/ConfiguratorCard.styles';
import tileStyles from '@/new-cars/common/SelectableTile.module.css';
import SectionName from '~/constants/new-cars/SectionName';
import { useIsMobile } from '~/context/common/DeviceContext';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import type { TrimResult } from '~/contracts/new-cars';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import { isFallbackImage } from '~/utilities/isFallbackImage';
import optimizeCardImage from '~/utilities/new-cars/optimizeCardImage';
import {
  ColorSwatch,
  ColorSwatchesContainer,
  Content,
  Description,
  HighlightItem,
  HighlightsList,
  HighlightsSection,
  Label,
  PriceAdd,
  UpgradesSection,
  ViewHighlightsRow
} from './TrimCard.styles';

type OwnProps = {
  areHighlightsExpanded: boolean;
  disabled?: boolean;
  index: number;
  selected: boolean;
  setExpandHighlightsIndex: React.Dispatch<React.SetStateAction<number | null>>;
  trim: TrimResult;
};

export default function TrimCard({
  areHighlightsExpanded,
  disabled = false,
  index,
  selected,
  setExpandHighlightsIndex,
  trim
}: OwnProps) {
  const { isFirstSection, setSelectedTrim } = useContext(ParentModelContext);
  const isMobile = useIsMobile();
  const [isImageBroken, setIsImageBroken] = useState(false);
  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = isMobile ? localExpanded : areHighlightsExpanded;
  const toggleExpanded = isMobile
    ? () => setLocalExpanded(prev => !prev)
    : () => setExpandHighlightsIndex(areHighlightsExpanded ? null : index);

  const {
    count,
    description,
    exteriorColors,
    imageUrl,
    lowestPrice,
    narrative: { adds, availableUpgrades = [], highlights = [] } = {},
    priceAdd,
    title
  } = trim;

  const isFallback = isFallbackImage(imageUrl);
  const optimizedImageUrl = optimizeCardImage(imageUrl, isFallback);

  return (
    <SelectableTile
      className={cx(
        'grid grid-rows-[auto_1fr] sm:grid-rows-subgrid sm:row-span-8 sm:gap-y-0 sm:mb-md',
        tileStyles.tile
      )}
      disabled={disabled}
      selected={selected}
      onClick={() => {
        setSelectedTrim(omit({ ...trim, ...trim.next }, 'next'));
      }}
      data-analytics-attributes={JSON.stringify({
        level: 'Trim',
        section: 'Trim',
        text: title,
        tileAction: selected ? 'selected' : 'deselected'
      })}
      data-analytics-id="New Cars"
      data-testid="trim-card"
      value={title}
      variant="simple-select"
    >
      <ImageContainer
        className="sm:row-start-1"
        disabled={disabled}
        isFallback={isFallback}
      >
        {optimizedImageUrl && !isImageBroken ? (
          <CardImage
            alt={title}
            data-testid="trim-card-image"
            loading="lazy"
            onError={() => setIsImageBroken(true)}
            src={optimizedImageUrl}
          />
        ) : (
          <ImageUnavailable>Image unavailable</ImageUnavailable>
        )}
      </ImageContainer>
      <Content className="sm:row-start-2 sm:row-end-9">
        <HeadlineRow className="sm:row-start-1 sm:mt-lg">
          <Title disabled={disabled}>{title}</Title>
          {isFirstSection(SectionName.Trim) ? (
            <PriceContainer data-testid="trim-card-price">
              <PriceLabel>from </PriceLabel>
              <PriceValue disabled={disabled}>
                ${formatAndDropDecimal(lowestPrice)}
              </PriceValue>
            </PriceContainer>
          ) : (
            <PriceAdd disabled={disabled} data-testid="trim-card-price-add">
              +${formatAndDropDecimal(priceAdd)}
            </PriceAdd>
          )}
        </HeadlineRow>
        <Description className="sm:row-start-2">
          {adds ? (
            <>
              {index > 0 && <Label>Adds: </Label>}
              {adds}
            </>
          ) : (
            description
          )}
        </Description>
        {isExpanded && highlights.length > 0 && (
          <HighlightsSection className="sm:row-start-3">
            <Label>Highlights</Label>

            <HighlightsList>
              {highlights.map((highlight, idx) => (
                <HighlightItem key={idx}>{highlight}</HighlightItem>
              ))}
            </HighlightsList>
          </HighlightsSection>
        )}
        {isExpanded && availableUpgrades.length > 0 && (
          <UpgradesSection className="sm:row-start-4">
            <Label>Available Upgrades</Label>

            <HighlightsList>
              {availableUpgrades.map((availableUpgrade, idx) => (
                <HighlightItem key={idx}>{availableUpgrade}</HighlightItem>
              ))}
            </HighlightsList>
          </UpgradesSection>
        )}
        {highlights.length + availableUpgrades.length > 0 && (
          <ViewHighlightsRow className="sm:row-start-5">
            <LinkButton
              size="sm"
              underline={false}
              className="flex gap-sm items-center w-fit"
              onClick={ev => {
                ev.stopPropagation();
                toggleExpanded();
              }}
              data-testid="trim-card-view-highlights-button"
              data-analytics-id="New Cars"
              data-analytics-attributes={JSON.stringify({
                level: 'Trim',
                section: 'Trim',
                text: 'View Highlights',
                action: isExpanded ? 'collapse' : 'expand'
              })}
            >
              {isExpanded ? 'Hide' : 'View Highlights'}
              <ChevronDown
                size={16}
                className={cx('text-icon-primary', isExpanded && 'rotate-180')}
              />
            </LinkButton>
          </ViewHighlightsRow>
        )}
        {exteriorColors.length > 0 && (
          <ColorSwatchesContainer
            className="sm:row-start-6"
            data-testid="trim-card-color-swatches"
          >
            {exteriorColors.map(color => (
              <ColorSwatch
                aria-label={color.name}
                key={color.code}
                role="img"
                style={{ backgroundColor: color.hexCode }}
                title={color.name}
              />
            ))}
          </ColorSwatchesContainer>
        )}
        <StockCount
          className="sm:row-start-7 sm:mt-lg"
          data-testid="trim-card-stock-count"
        >
          {count} available
        </StockCount>
      </Content>
    </SelectableTile>
  );
}
